import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Package, CheckCircle2, Clock } from "lucide-react";
import { statusLabel } from "@/components/orders/orderMeta";
import { BuyerOrderPanel } from "@/components/orders/BuyerOrderPanel";
import { money, StatusPill } from "@/components/marketplace/shared";
import { ReviewSheet, ReviewAction, useEligible } from "@/components/marketplace/Reviews";
import { PushOptInSheet, usePushOptIn } from "@/components/PushOptInSheet";
import { MessageButton } from "@/components/messages/shared";

export const ORDER_STATUS_LABEL = {
  pending: "Aguarda pagamento", paid: "Pago", processing: "Em preparação", ready: "Pronto para levantar", shipped: "Enviado",
  completed: "Concluído", cancelled: "Cancelado", refunded: "Reembolsado", partially_refunded: "Reembolso parcial", disputed: "Em disputa",
};

function variantLabel(v) {
  if (!v) return "";
  if (typeof v === "string") return ` (${v})`;
  if (Array.isArray(v)) return v.length ? ` (${v.map(String).join(", ")})` : "";
  if (typeof v === "object") { const vals = Object.values(v).filter(Boolean).map(String); return vals.length ? ` (${vals.join(", ")})` : ""; }
  return "";
}

export function LoadError({ onRetry, text = "Não foi possível carregar. Verifica a ligação e tenta novamente." }) {
  return (
    <div data-testid="load-error" className="bg-white rounded-2xl border border-red-200 p-4 text-center">
      <p className="text-sm text-red-800 mb-2">{text}</p>
      <button onClick={onRetry} data-testid="load-error-retry" className="px-4 py-2 rounded-xl bg-black text-white text-xs font-semibold">Tentar novamente</button>
    </div>
  );
}

export function OrderCard({ o, children }) {
  return (
    <div data-testid={`order-${o.order_number}`} className="bg-white rounded-2xl border border-zinc-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-sm">#{o.order_number}</div>
          <div className="text-[11px] text-zinc-500">{o.seller_name || "—"} · {o.created_at && !Number.isNaN(Date.parse(o.created_at)) ? new Date(o.created_at).toLocaleDateString("pt-PT") : "—"}</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-sm">{money(o.total_minor, o.currency)}</div>
          <StatusPill status={o.status} map={{ ...ORDER_STATUS_LABEL, [o.status]: statusLabel(o) }} />
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {(Array.isArray(o.items) ? o.items : []).map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-8 h-8 rounded bg-zinc-100 overflow-hidden shrink-0">{it.photo && <img src={it.photo} alt="" className="w-full h-full object-cover" />}</div>
            <span className="flex-1 truncate">{it.qty}× {it.name}{variantLabel(it.variant)}</span>
            <span className={it.service_id ? "text-[#c8102e] font-semibold" : "text-zinc-500"}>{it.service_id ? (o.quote_id ? "Serviço (orçamento)" : "Serviço") : it.fulfilment_mode === "delivery" ? "Entrega" : "Levantamento"}</span>
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

export default function MyOrders() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [orders, setOrders] = useState(null);
  const [pay, setPay] = useState(sessionId ? "pending" : null);

  const { items: elig, reload: reloadElig } = useEligible(true);
  const [sheet, setSheet] = useState(null);
  const optin = usePushOptIn();
  useEffect(() => { if (orders?.length) optin.trigger(); /* eslint-disable-next-line */ }, [orders?.length]);
  const [loadErr, setLoadErr] = useState(false);
  const load = () => { setLoadErr(false); return api.get("/orders/mine").then(r => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => { setLoadErr(true); setOrders([]); }); };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setPay("paid"); load(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setPay("failed"); return; }
      } catch { setPay("failed"); return; }
      if (++tries < 8) timer = setTimeout(poll, 2000); else setPay("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="page fade-up">
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Marketplace</Link>
      <div className="flex items-center justify-between mb-3"><h1 className="font-display text-2xl font-extrabold">Minhas compras</h1>
        <Link to="/marketplace/orcamentos" data-testid="orders-quotes-link" className="text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:border-black">Orçamentos</Link>
        <Link to="/minhas-reservas" data-testid="orders-reservations-link" className="text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:border-black ml-1">Reservas</Link></div>
      {pay === "pending" && <div data-testid="pay-pending" className="mb-3 p-3 rounded-xl bg-yellow-50 border border-yellow-300 text-xs text-yellow-900 flex items-center gap-2"><Clock size={14} /> A confirmar o pagamento…</div>}
      {pay === "paid" && <div data-testid="pay-success" className="mb-3 p-3 rounded-xl bg-green-50 border border-green-300 text-xs text-green-800 flex items-center gap-2"><CheckCircle2 size={14} /> Pagamento confirmado! Os vendedores foram notificados.</div>}
      {(pay === "failed" || pay === "timeout") && <div data-testid="pay-failed" className="mb-3 p-3 rounded-xl bg-red-50 border border-red-300 text-xs text-red-800">Não conseguimos confirmar o pagamento ainda. Se foste cobrado, a encomenda será actualizada automaticamente.</div>}
      {loadErr && <div className="mb-3"><LoadError onRetry={load} text="Não foi possível carregar as tuas encomendas." /></div>}
      {orders === null && !loadErr && <p className="text-sm text-zinc-500 text-center py-8" data-testid="orders-loading">A carregar…</p>}
      {!loadErr && orders?.length === 0 && (
        <div data-testid="orders-empty" className="text-center py-12 text-zinc-500 text-sm">
          <Package size={28} className="mx-auto mb-2 text-zinc-300" />
          Ainda não fizeste compras. As tuas encomendas aparecerão aqui, separadas por vendedor.
        </div>
      )}
      <div className="space-y-2 mb-8">{orders?.map(o => {
        const exps = elig.filter(e => e.source_type === "order" && e.source_id === o.id);
        return <OrderCard key={o.id} o={o}>
          <BuyerOrderPanel o={o} reload={load} />
          <div className="mt-2"><MessageButton context_type="order" context_id={o.id} label="Mensagem à loja" /></div>
          {exps.length > 0 && <div data-testid={`order-reviews-${o.order_number}`} className="mt-2 pt-2 border-t border-zinc-100 space-y-1.5">
            {exps.map(e => <div key={e.target_id} className="flex items-center justify-between gap-2 text-xs"><span className="truncate text-zinc-600">{e.target_name}</span><ReviewAction exp={e} onOpen={setSheet} /></div>)}
          </div>}
        </OrderCard>;
      })}</div>
      {sheet && <ReviewSheet exp={sheet} onClose={() => setSheet(null)} onDone={() => { setSheet(null); reloadElig(); }} />}
      <PushOptInSheet open={optin.show} onClose={optin.close} />
    </div>
  );
}
